import React from 'react'
import './unifiedUI.scss'
import screenAnnotationsData from './screenAnnotationsData.json'

/**
 * IntroTextBox Component
 * Displays project/section intro text with title, tags, description, and metadata
 * Reusable across workGallery and aboutGallery
 */
const IntroTextBox = ({ 
  subsectionInfo, 
  isFadingOut = false,
  annotationsEnabled = true,
  setAnnotationsEnabled,
  selectedClient,
  selectedProject,
  currentLevel
}) => {
  if (!subsectionInfo) return null

  const { subsection, section } = subsectionInfo
  
  // Detect mobile viewport
  const [isMobile, setIsMobile] = React.useState(false)
  
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Check if this subsection has annotations
  const hasAnnotations = React.useMemo(() => {
    console.log('🔍 Checking for annotations:', {
      currentLevel,
      selectedClient,
      selectedProject,
      sectionId: section?.id,
      subsectionId: subsection?.id
    })
    
    if (currentLevel !== 3 || !selectedClient || !selectedProject || !section) {
      console.log('❌ Early return: missing requirements')
      return false
    }
    
    try {
      const projectAnnotations = screenAnnotationsData[selectedClient]?.[selectedProject]
      if (!projectAnnotations) {
        console.log('❌ No project annotations')
        return false
      }
      
      const sectionAnnotations = projectAnnotations[section.id]
      if (!sectionAnnotations) {
        console.log('❌ No section annotations for:', section.id)
        return false
      }
      
      const subsectionAnnotations = sectionAnnotations[subsection.id]
      const hasAnno = Array.isArray(subsectionAnnotations) && subsectionAnnotations.length > 0
      console.log('✅ Has annotations:', hasAnno, subsectionAnnotations?.length || 0)
      return hasAnno
    } catch (e) {
      console.log('❌ Error checking annotations:', e)
      return false
    }
  }, [currentLevel, selectedClient, selectedProject, section, subsection])

  return (
    <div className={`introText-overlay ${isFadingOut ? 'fade-out' : ''}`}>
        {/* Title */}
        <div className="intro-title-wrapper">
          <h4 className="intro-title">{subsection.title}</h4>
        </div>
        
        {/* Tags */}
        {subsection.tags && subsection.tags.length > 0 && (
          <div className="intro-tags-container">
            {subsection.tags.map((tag, index) => {
              const tagLabel = typeof tag === 'string' ? tag : tag.label
              const tagType = typeof tag === 'string' ? 'default' : (tag.type || 'default').toLowerCase()
              return (
                <span key={index} className={`intro-tag-pill ${tagType}`}>
                  {tagLabel}
                </span>
              )
            })}
          </div>
        )}
        
        {/* Intro Text */}
        {subsection.intro && (
          <p className="intro-main-text">{subsection.intro}</p>
        )}
        
        {/* Description (if different from intro) - hidden on mobile */}
        {!isMobile && subsection.description && subsection.description !== subsection.intro && (
          <p className="intro-description">{subsection.description}</p>
        )}
        
        {/* Metadata Table (Role, Year, Type) */}
        {(subsection.role || subsection.year || subsection.type) && (
          <div className="intro-info-container">
            {subsection.role && (
              <div className="info-column">
                <p className="info-cell info-cell-header">ROLE</p>
                <p className="info-cell">{subsection.role}</p>
              </div>
            )}

            {subsection.year && (
              <div className="info-column">
                <p className="info-cell info-cell-header">YEAR</p>
                <p className="info-cell">{subsection.year}</p>
              </div>
            )}
            
            {subsection.type && (
              <div className="info-column">
                <p className="info-cell info-cell-header">TYPE</p>
                <p className="info-cell">{subsection.type}</p>
              </div>
            )}
          </div>
        )}
        
        {/* Annotation Toggle - only show if subsection has annotations */}
        {hasAnnotations && setAnnotationsEnabled && (
          <div className="annotation-toggle-container">
            <button 
              className={`annotation-toggle ${annotationsEnabled ? 'active' : ''}`}
              onClick={() => setAnnotationsEnabled(!annotationsEnabled)}
              aria-label={annotationsEnabled ? 'Hide annotations' : 'Show annotations'}
            >
              <span className="annotation-toggle-label">ANNOTATIONS</span>
              <span className="annotation-toggle-indicator">
                {annotationsEnabled ? '●' : '○'}
              </span>
            </button>
          </div>
        )}
    </div>
  )
}

export default IntroTextBox
